import { X, AlertTriangle, AlertCircle } from 'lucide-react';
import { useEffect } from 'react';
import { IconButton } from '../../Button';
import type { ValidationResult } from '../types';

interface UploadValidationBannerProps {
  errors: ValidationResult[];
  warnings: ValidationResult[];
  onDismissErrors: () => void;
  onDismissWarnings: () => void;
}

export const UploadValidationBanner = ({ errors, warnings, onDismissErrors, onDismissWarnings }: UploadValidationBannerProps) => {
  useEffect(() => {
    if (warnings.length === 0) return;
    const timer = setTimeout(onDismissWarnings, 8000);
    return () => clearTimeout(timer);
  }, [warnings, onDismissWarnings]);

  if (errors.length === 0 && warnings.length === 0) return null;

  return (
    <div className="space-y-2 mb-3">
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                {errors.map((e, i) => (
                  <div key={i}>
                    <p className="text-sm font-medium text-red-800">{e.title}</p>
                    <p className="text-xs text-red-600">{e.message}</p>
                    {e.suggestion && <p className="text-xs text-red-500 mt-1">{e.suggestion}</p>}
                  </div>
                ))}
              </div>
            </div>
            <IconButton size="xs" onClick={onDismissErrors} className="text-red-400 hover:text-red-600">
              <X className="w-3.5 h-3.5" />
            </IconButton>
          </div>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                {warnings.map((w, i) => (
                  <div key={i}>
                    <p className="text-sm font-medium text-amber-800">{w.title}</p>
                    <p className="text-xs text-amber-600">{w.message}</p>
                  </div>
                ))}
              </div>
            </div>
            <IconButton size="xs" onClick={onDismissWarnings} className="text-amber-400 hover:text-amber-600">
              <X className="w-3.5 h-3.5" />
            </IconButton>
          </div>
        </div>
      )}
    </div>
  );
};
